import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, MagnifyingGlassIcon, MinusIcon, PlayIcon, PlusIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Field, getApiErrorMessage, inputClass, inputErrorClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../store/auth.store';
import { cn } from '../../lib/utils';

interface WorkCentre {
  id: string;
  code: string | null;
  name: string;
  open: number;
  running: number;
}

interface Run {
  id: string;
  operatorName: string;
  startedAt: string;
}

interface Step {
  id: string;
  workOrderId: number;
  workOrderNumber: string;
  plannedEndDate: string | null;
  productName: string;
  sku: string;
  unit: string | null;
  sequence: number;
  name: string;
  plannedQuantity: number;
  inputQuantity: number;
  goodQuantity: number;
  rejectedQuantity: number;
  inReworkQuantity: number;
  remainingQuantity: number;
  ready: boolean;
  runs: Run[];
}

interface ScrapReason {
  id: string;
  name: string;
}

// Remembered on this device: a tablet usually stays at one work centre with one operator
const remember = {
  get: (key: string) => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  },
  set: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // only for this visit then
    }
  },
};

const elapsed = (since: string, now: number) => {
  const minutes = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000));
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours} h ${String(minutes % 60).padStart(2, '0')} min` : `${minutes} min`;
};

const dueText = (value: string | null) => {
  if (!value) return 'No due date';
  // A plain date is a day here; a full timestamp is read as the moment it is, then taken as a local day
  const due = new Date(String(value).length <= 10 ? `${value}T00:00:00` : String(value));
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);
  return days < 0 ? `${-days} day${days === -1 ? '' : 's'} late` : days === 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`;
};

// A number to enter with a thumb: big minus and plus around the box
const Stepper: React.FC<{ id: string; label: string; value: string; onChange: (value: string) => void; max?: number; tone?: string }> = ({ id, label, value, onChange, max, tone }) => {
  const number = Number(value) || 0;
  const set = (next: number) => onChange(String(Math.max(0, max === undefined ? next : Math.min(max, next))));
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1 flex items-stretch">
        <button type="button" onClick={() => set(number - 1)} aria-label={`One less ${label.toLowerCase()}`} className="rounded-l-md border border-gray-300 bg-gray-50 px-4 text-gray-700 hover:bg-gray-100 active:bg-gray-200">
          <MinusIcon className="h-6 w-6" />
        </button>
        <input id={id} type="number" inputMode="numeric" min={0} step={1} value={value} onChange={(event) => onChange(event.target.value)} className={cn('w-full border-y border-gray-300 py-3 text-center text-2xl font-semibold tabular-nums focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500', tone)} />
        <button type="button" onClick={() => set(number + 1)} aria-label={`One more ${label.toLowerCase()}`} className="rounded-r-md border border-gray-300 bg-gray-50 px-4 text-gray-700 hover:bg-gray-100 active:bg-gray-200">
          <PlusIcon className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

const FinishForm: React.FC<{ step: Step; run: Run; now: number; isPending: boolean; onCancel: () => void; onSubmit: (body: Record<string, unknown>) => void }> = ({ step, run, now, isPending, onCancel, onSubmit }) => {
  const [fromRework, setFromRework] = useState(step.remainingQuantity === 0 && step.inReworkQuantity > 0);
  const [good, setGood] = useState('0');
  const [rejected, setRejected] = useState('0');
  const [rework, setRework] = useState('0');
  const [reasonId, setReasonId] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [notes, setNotes] = useState('');
  const [asked, setAsked] = useState(false);
  const { data: reasons = [] } = useQuery<ScrapReason[]>({ queryKey: ['scrap-reasons', 'active'], queryFn: async () => (await api.get('/manufacturing/scrap-reasons', { params: { activeOnly: 'true' } })).data });

  const limit = fromRework ? step.inReworkQuantity : step.remainingQuantity;
  const counts = { good: Number(good), rejected: Number(rejected), rework: fromRework ? 0 : Number(rework) };
  const whole = Object.values(counts).every((value) => Number.isInteger(value) && value >= 0);
  const total = counts.good + counts.rejected + counts.rework;
  const errors = {
    counts: !whole ? 'Enter whole numbers' : total > limit ? `Only ${limit} can be booked ${fromRework ? 'out of rework' : 'here'}` : undefined,
    reason: counts.rejected > 0 && !reasonId && !reasonText.trim() ? 'Say why pieces are rejected' : undefined,
  };

  return (
    <form
      noValidate
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        setAsked(true);
        if (errors.counts || errors.reason) return;
        onSubmit({
          goodQuantity: counts.good,
          rejectedQuantity: counts.rejected,
          ...(fromRework ? { fromRework: true } : { reworkQuantity: counts.rework }),
          ...(counts.rejected > 0 ? { scrapReasonId: reasonId || null, rejectionReason: reasonText.trim() || null } : {}),
          notes: notes.trim() || null,
        });
      }}
    >
      <div className="rounded-md bg-indigo-50 p-4 text-sm text-indigo-900">
        <p className="text-base font-semibold">{step.workOrderNumber} · {step.sequence} {step.name}</p>
        <p>{run.operatorName}, working {elapsed(run.startedAt, now)}. The time is booked from Start to now.</p>
      </div>

      {step.inReworkQuantity > 0 && (
        <div className="flex gap-2" role="radiogroup" aria-label="What was worked on">
          {[
            { value: false, label: `New pieces (${step.remainingQuantity} waiting)`, disabled: step.remainingQuantity === 0 },
            { value: true, label: `Rework (${step.inReworkQuantity} waiting)`, disabled: false },
          ].map((option) => (
            <button key={String(option.value)} type="button" role="radio" aria-checked={fromRework === option.value} disabled={option.disabled} onClick={() => setFromRework(option.value)} className={cn('flex-1 rounded-md px-3 py-3 text-sm font-medium ring-1 ring-inset', fromRework === option.value ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 disabled:opacity-40')}>
              {option.label}
            </button>
          ))}
        </div>
      )}

      <div className={cn('grid gap-4', fromRework ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
        <Stepper id="sf-good" label="Good" value={good} onChange={setGood} max={limit} tone="text-green-700" />
        <Stepper id="sf-rejected" label="Rejected" value={rejected} onChange={setRejected} max={limit} tone="text-red-700" />
        {!fromRework && <Stepper id="sf-rework" label="To rework" value={rework} onChange={setRework} max={limit} tone="text-amber-700" />}
      </div>
      <p className={cn('text-sm', asked && errors.counts ? 'text-red-600' : 'text-gray-500')}>{asked && errors.counts ? errors.counts : `${total} of ${limit} ${fromRework ? 'in rework' : 'waiting'} booked`}</p>

      {counts.rejected > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Reason for rejection" htmlFor="sf-reason" error={asked ? errors.reason : undefined}>
            <select id="sf-reason" value={reasonId} onChange={(event) => setReasonId(event.target.value)} className={cn(inputClass, 'py-3', asked && errors.reason && inputErrorClass)}>
              <option value="">Choose a reason</option>
              {reasons.map((reason) => (<option key={reason.id} value={reason.id}>{reason.name}</option>))}
            </select>
          </Field>
          <Field label="In words" htmlFor="sf-reason-text">
            <input id="sf-reason-text" type="text" maxLength={300} value={reasonText} onChange={(event) => setReasonText(event.target.value)} className={cn(inputClass, 'py-3')} />
          </Field>
        </div>
      )}
      <Field label="Notes" htmlFor="sf-notes">
        <input id="sf-notes" type="text" maxLength={500} value={notes} onChange={(event) => setNotes(event.target.value)} className={cn(inputClass, 'py-3')} />
      </Field>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} disabled={isPending} className="flex-1 rounded-md bg-white px-4 py-4 text-base font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Back</button>
        <button type="submit" disabled={isPending} className="flex-[2] rounded-md bg-indigo-600 px-4 py-4 text-base font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">{isPending ? 'Booking…' : 'Book and Finish'}</button>
      </div>
    </form>
  );
};

const ShopFloor: React.FC = () => {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canWork = can('manufacturing.update');
  const user = useAuthStore((state) => state.user) as { firstName?: string; lastName?: string } | null;
  const [centre, setCentre] = useState(() => remember.get('shopFloorCentre'));
  const [operator, setOperator] = useState(() => remember.get('shopFloorOperator') || [user?.firstName, user?.lastName].filter(Boolean).join(' '));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(Date.now());
  const [finishing, setFinishing] = useState<{ step: Step; run: Run } | null>(null);
  const [cancelling, setCancelling] = useState<{ step: Step; run: Run } | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: centres = [] } = useQuery<WorkCentre[]>({ queryKey: ['shop-floor', 'centres'], queryFn: async () => (await api.get('/shop-floor/work-centers')).data, refetchInterval: 30000 });
  const { data: steps = [], isLoading, error, refetch, isFetching } = useQuery<Step[]>({
    queryKey: ['shop-floor', 'queue', centre, search],
    queryFn: async () => (await api.get('/shop-floor/queue', { params: { ...(centre && centre !== 'all' ? { workCenterId: centre } : {}), ...(search ? { search } : {}) } })).data,
    enabled: !!centre,
    refetchInterval: 30000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['shop-floor'] });
  const failed = (fallback: string) => (cause: unknown) => toast.error(getApiErrorMessage(cause, fallback), { duration: 8000 });

  const start = useMutation({
    mutationFn: async (step: Step) => (await api.post(`/shop-floor/operations/${step.id}/start`, { operatorName: operator.trim() })).data,
    onSuccess: (_run, step) => {
      toast.success(`${operator.trim()} started ${step.name} on ${step.workOrderNumber}`);
      refresh();
    },
    onError: failed('Could not start'),
  });
  const finish = useMutation({
    mutationFn: async ({ run, body }: { run: Run; body: Record<string, unknown> }) => (await api.post(`/shop-floor/runs/${run.id}/finish`, body)).data as { minutes: number },
    onSuccess: (result) => {
      toast.success(`Booked: ${Math.round(result.minutes)} min`);
      setFinishing(null);
      refresh();
      queryClient.invalidateQueries({ queryKey: ['jobCards'] });
    },
    onError: failed('Could not book'),
  });
  const cancel = useMutation({
    mutationFn: async (run: Run) => (await api.post(`/shop-floor/runs/${run.id}/cancel`)).data,
    onSuccess: () => {
      toast.success('Run cancelled; nothing was booked');
      setCancelling(null);
      refresh();
    },
    onError: failed('Could not cancel'),
  });

  const chooseCentre = (id: string) => {
    setCentre(id);
    remember.set('shopFloorCentre', id);
  };
  const operatorValid = operator.trim().length >= 2;
  const mine = (step: Step) => step.runs.find((run) => run.operatorName.toLowerCase() === operator.trim().toLowerCase());
  const centreName = centre === 'all' ? 'All work centres' : centres.find((item) => item.id === centre)?.name ?? 'Loading…';

  // First the screen is told where it stands
  if (!centre || (centre !== 'all' && centres.length > 0 && !centres.some((item) => item.id === centre))) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Shop Floor</h1>
          <p className="mt-1 text-sm text-gray-500">Choose the work centre this screen stands at. It is remembered on this device.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {centres.map((item) => (
            <button key={item.id} type="button" onClick={() => chooseCentre(item.id)} className="rounded-lg bg-white p-5 text-left shadow-sm ring-1 ring-gray-200 hover:ring-indigo-400 active:bg-indigo-50" data-testid="centre-choice">
              <p className="text-lg font-semibold text-gray-900">{item.name}</p>
              {item.code && <p className="font-mono text-sm text-gray-500">{item.code}</p>}
              <p className="mt-3 text-sm text-gray-600">{item.open} step{item.open === 1 ? '' : 's'} open · {item.running} running</p>
            </button>
          ))}
          <button type="button" onClick={() => chooseCentre('all')} className="rounded-lg border-2 border-dashed border-gray-300 p-5 text-left text-gray-600 hover:border-indigo-400" data-testid="centre-choice">
            <p className="text-lg font-semibold">All work centres</p>
            <p className="mt-3 text-sm">Every step on the floor, for a supervisor</p>
          </button>
        </div>
        {centres.length === 0 && <p className="text-sm text-gray-500">No work order is in progress yet, so there is nothing on the floor.</p>}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Shop Floor · {centreName}</h1>
          <button type="button" onClick={() => chooseCentre('')} className="mt-1 text-sm font-medium text-indigo-600 hover:text-indigo-800">Change work centre</button>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Field label="Operator" htmlFor="sf-operator" error={!operatorValid ? 'Enter who is working' : undefined}>
            <input id="sf-operator" type="text" maxLength={100} value={operator} onChange={(event) => { setOperator(event.target.value); remember.set('shopFloorOperator', event.target.value.trim()); }} className={cn(inputClass, 'py-3 text-base sm:w-56', !operatorValid && inputErrorClass)} />
          </Field>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Scan or type a work order" aria-label="Find a work order" className={cn(inputClass, 'py-3 pl-10 text-base sm:w-72')} />
          </div>
          <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'py-3')} aria-label="Refresh">
            <ArrowPathIcon className={cn('h-5 w-5', isFetching && 'animate-spin')} />
          </button>
        </div>
      </div>

      {error ? (
        <p className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow-sm ring-1 ring-gray-200">{getApiErrorMessage(error, 'The steps could not be loaded')}</p>
      ) : isLoading ? (
        <p className="p-8 text-center text-sm text-gray-500">Loading…</p>
      ) : steps.length === 0 ? (
        <div className="rounded-lg bg-white p-10 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-base font-medium text-gray-900">{search ? 'No step matches this work order' : 'Nothing is waiting here'}</p>
          <p className="mt-1 text-sm text-gray-500">Steps appear when their work order is started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {steps.map((step) => {
            const myRun = mine(step);
            const late = dueText(step.plannedEndDate).includes('late');
            return (
              <div key={step.id} className={cn('rounded-lg bg-white p-4 shadow-sm ring-1', step.runs.length ? 'ring-2 ring-green-500' : 'ring-gray-200')} data-testid="floor-step">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-gray-900">{step.sequence} · {step.name}</p>
                    <p className="truncate text-sm text-gray-700"><span className="font-mono">{step.workOrderNumber}</span> · {step.productName} ({step.sku})</p>
                  </div>
                  <span className={cn('whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium', late ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700')}>{dueText(step.plannedEndDate)}</span>
                </div>
                <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
                  {[
                    { label: 'Waiting', value: step.remainingQuantity, tone: 'text-gray-900' },
                    { label: 'Good', value: step.goodQuantity, tone: 'text-green-700' },
                    { label: 'Rejected', value: step.rejectedQuantity, tone: 'text-red-700' },
                    { label: 'In rework', value: step.inReworkQuantity, tone: 'text-amber-700' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-gray-50 py-2">
                      <dt className="text-xs text-gray-500">{item.label}</dt>
                      <dd className={cn('text-xl font-semibold tabular-nums', item.tone)}>{item.value}</dd>
                    </div>
                  ))}
                </dl>
                {step.runs.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm" data-testid="floor-runs">
                    {step.runs.map((run) => (
                      <li key={run.id} className="flex items-center justify-between rounded-md bg-green-50 px-3 py-2 text-green-900">
                        <span><span className="font-medium">{run.operatorName}</span> · working {elapsed(run.startedAt, now)}</span>
                        {canWork && (
                          <span className="flex gap-2">
                            <button type="button" onClick={() => setFinishing({ step, run })} className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-green-800 ring-1 ring-inset ring-green-300 hover:bg-green-100">Finish</button>
                            <button type="button" onClick={() => setCancelling({ step, run })} aria-label={`Cancel the run of ${run.operatorName}`} className="rounded-md px-2 py-1.5 text-green-800 hover:bg-green-100"><XMarkIcon className="h-5 w-5" /></button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {canWork && (
                  <div className="mt-4">
                    {myRun ? (
                      <button type="button" onClick={() => setFinishing({ step, run: myRun })} className="flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-4 text-base font-semibold text-white hover:bg-green-500">
                        <StopIcon className="h-6 w-6" />
                        Finish {step.name}
                      </button>
                    ) : (
                      <button type="button" onClick={() => start.mutate(step)} disabled={!step.ready || !operatorValid || start.isPending} title={!step.ready ? 'Nothing has reached this step yet' : undefined} className="flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-4 text-base font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-300">
                        <PlayIcon className="h-6 w-6" />
                        {step.ready ? `Start ${step.name}` : 'Nothing waiting yet'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-center text-xs text-gray-500">
        Times are booked from Start to Finish. To book a card after the fact, use <Link to="/job-cards" className="font-medium text-indigo-600 hover:text-indigo-800">Job Cards</Link>.
      </p>

      <Modal isOpen={!!finishing} onClose={() => !finish.isPending && setFinishing(null)} title="Finish" size="xl">
        {finishing && (
          <FinishForm key={finishing.run.id} step={finishing.step} run={finishing.run} now={now} isPending={finish.isPending} onCancel={() => setFinishing(null)} onSubmit={(body) => finish.mutate({ run: finishing.run, body })} />
        )}
      </Modal>
      <Modal isOpen={!!cancelling} onClose={() => !cancel.isPending && setCancelling(null)} title="Cancel this run?" size="md">
        {cancelling && (
          <div className="space-y-5">
            <p className="text-sm text-gray-700">The run of {cancelling.run.operatorName} on {cancelling.step.name} ({cancelling.step.workOrderNumber}) is closed without booking any time or pieces. Use this only for a run started by mistake.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setCancelling(null)} className="flex-1 rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">Keep It</button>
              <button type="button" onClick={() => cancel.mutate(cancelling.run)} disabled={cancel.isPending} className="flex-1 rounded-md bg-red-600 px-4 py-3 text-base font-semibold text-white hover:bg-red-500 disabled:opacity-50">{cancel.isPending ? 'Cancelling…' : 'Cancel Run'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ShopFloor;
